import { useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactElement } from "react";
import type { Rules } from "odyssey-shared";
import { ConfirmDialog, KitInputProvider, PlayWindow, Tabs, windowRoom, type KitInput, type PlaceSource, type SizeSource } from "../../ui/kit/index.ts";
import type { PlaceSize } from "../../ui/kit/place.ts";
import { say } from "../../words.ts";
import type { WindowOrder } from "../windows/order.ts";
import { windowPlace, windowSize } from "../windows/places.ts";
import { useRaise } from "../windows/raise.ts";
import { isTouchPage } from "../touch/page.ts";
import { pendingTotal } from "./allocation.ts";
import { NO_INSETS, measureSafeInsets, phoneRoom, type SafeInsets } from "./safe-area.ts";
import { CharacterPage, type CharacterHeader } from "./character-page.tsx";
import { CompanionsPage, type PortraitOf } from "./companions-page.tsx";
import { PreviewPanel } from "./preview.tsx";
import "./character.css";
import type { CharacterStore } from "./store.ts";
import { visibleTabs, type CharacterTabId } from "./tabs.ts";

/**
 * The character window (ADR-138's amendment of 2026-10-31, decisions 1, 2, 5 and 9; P2.22-T25, P2.26-T24, P1.9-T221): a
 * play window of 300 by 236 at 14,44 on the 640 by 360 grid, its tabs from the registry (`tabs.ts`), the selected
 * tab's page, the preview panel beside it while points are pending, and the Spend and Respec dialogs. On a phone-sized
 * area it is one window filling the room, with the Equipment tab and the preview in the header's pane. Test-after.
 */

/**
 * The window's home box on the 640 by 360 grid.
 */
export const CHARACTER_WINDOW = Object.freeze({ left: 14, top: 44, width: 300, height: 236 });

/**
 * The tallest area drawn in the phone form: the phone design's 276-logical safe area and anything below 320.
 */
const PHONE_HEIGHT = 320;

/**
 * What the HUD hands the window.
 */
export interface HudCharacter {
  readonly source: CharacterStore;
  readonly rules: Rules;
  readonly onClose: () => void;
  readonly portraitOf: PortraitOf;
  /** The page's input router, which the Spend and Respec dialogs hold the keys with while open. */
  readonly input: KitInput;
  readonly order?: WindowOrder | undefined;
  readonly place?: PlaceSource | undefined;
  readonly size?: SizeSource | undefined;
}

/**
 * Whether the window draws its phone form: on a touch device (no hover, a coarse pointer; the mobile rules), or on an
 * area too short for the desktop window.
 *
 * @param area The HUD's logical area.
 * @param touch Whether the page is on a touch device.
 * @returns True for a phone-sized area.
 */
export function isPhoneArea(area: PlaceSize, touch: boolean): boolean {
  return touch || area.height < PHONE_HEIGHT;
}

/**
 * A skeleton tab's page.
 *
 * @returns The page.
 */
function NotYet(): ReactElement {
  return (
    <div className="ot-character__skeleton" data-character="skeleton">
      {say("character.notYet")}
    </div>
  );
}

/**
 * The window, while its store says it is open.
 *
 * @param props The HUD's area, the window's parts and the character's header.
 * @returns The window, or nothing while closed.
 */
export function CharacterWindow({ area, character, header }: { readonly area: PlaceSize; readonly character: HudCharacter; readonly header: CharacterHeader }): ReactElement | null {
  const store = character.source;
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const host = useRef<HTMLDivElement>(null);
  const front = useRaise(host, character.order, "character");
  const [insets, setInsets] = useState<SafeInsets>(NO_INSETS);
  useLayoutEffect(() => {
    if (snapshot.open) {
      setInsets(measureSafeInsets(host.current?.parentElement ?? null));
    }
  }, [snapshot.open, area.width, area.height]);
  if (!snapshot.open) {
    return null;
  }
  const phone = isPhoneArea(area, isTouchPage());
  const room = phone ? phoneRoom(area, insets) : windowRoom(area);
  const tabs = visibleTabs({ phone });
  const tab: CharacterTabId = tabs.some((entry) => entry.id === snapshot.tab) ? snapshot.tab : "character";
  const box = phone ? { left: room.left, top: room.top, width: room.right - room.left, height: room.bottom - room.top } : CHARACTER_WINDOW;
  const panelId = "ot-character-panel";
  const preview = tab === "character" && pendingTotal(snapshot.pending) > 0 ? <PreviewPanel snapshot={snapshot} rules={character.rules} level={header.level} /> : null;
  const pending = pendingTotal(snapshot.pending);
  return (
    <>
      <PlayWindow id="character" title={say("character.title")} onClose={character.onClose} box={box} room={room} place={phone ? undefined : (character.place ?? windowPlace("character"))} size={phone ? undefined : (character.size ?? windowSize("character"))} host={host} className={front ? "ot-character-window is-front" : "ot-character-window"} data={{ "data-character": phone ? "phone" : "window" }}>
        {() => (
          <div className={phone ? "ot-character ot-character--phone" : "ot-character"}>
            <Tabs label={say("character.title")} tabs={tabs.map((entry) => ({ id: entry.id, label: entry.label(), name: entry.label(), data: { "data-tab": entry.id } }))} value={tab} onChange={(id) => store.setTab(id)} tabId={(id) => `ot-character-tab-${id}`} panelId={panelId} />
            <div id={panelId} role="tabpanel" className="ot-character__panel">
              {tab === "character" ? (
                <CharacterPage snapshot={snapshot} store={store} rules={character.rules} header={header} phone={phone} aside={phone ? preview : undefined} />
              ) : tab === "companions" ? (
                <CompanionsPage snapshot={snapshot} store={store} portraitOf={character.portraitOf} />
              ) : (
                <NotYet />
              )}
            </div>
          </div>
        )}
      </PlayWindow>
      {!phone && preview !== null ? (
        <div className="ot-character-aside" data-character="aside" style={{ left: `calc(var(--px) * ${CHARACTER_WINDOW.left + CHARACTER_WINDOW.width + 4})`, top: `calc(var(--px) * ${CHARACTER_WINDOW.top})` }}>
          {preview}
        </div>
      ) : null}
      {snapshot.dialog === "spend" ? (
        <div className="ot-character-dialog">
          <KitInputProvider input={character.input}>
          <ConfirmDialog title={pending === 1 ? say("character.spendTitleOne") : say("character.spendTitle", { count: String(pending) })} confirmLabel={say("character.spend")} cancelLabel={say("character.cancel")} tone="primary" busy={snapshot.waiting} onConfirm={() => store.confirmSpend()} onCancel={() => store.ask("none")}>
            <p className="ot-character-dialog__body">{say("character.spendBody")}</p>
          </ConfirmDialog>
          </KitInputProvider>
        </div>
      ) : null}
      {snapshot.dialog === "respec" ? (
        <div className="ot-character-dialog">
          <KitInputProvider input={character.input}>
          <ConfirmDialog title={say("character.respecTitle")} confirmLabel={say("character.respecConfirm")} cancelLabel={say("character.cancel")} busy={snapshot.waiting} onConfirm={() => store.confirmRespec()} onCancel={() => store.ask("none")}>
            <p className="ot-character-dialog__body">{say("character.respecBody")}</p>
          </ConfirmDialog>
          </KitInputProvider>
        </div>
      ) : null}
    </>
  );
}
